import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { EvaService } from "src/app/core";
import { Formula, Formulas } from "src/app/features/formula/models/formula.model";
import { EvaSubscriptionService } from "src/app/features/subscription/eva-subscription.service";
import { EvaSubscription } from "src/app/features/subscription/models/eva-subscription.model";

import { Component, DestroyRef, inject, OnInit } from "@angular/core";

const WHATSAPP_EXPERT_LINK = "https://api.whatsapp.com/send/?phone=%2B41783189046&text&type=phone_number&app_absent=0";

@Component({
  selector: "app-formula-popup",
  templateUrl: "./formula-popup.component.html",
  styleUrl: "./formula-popup.component.scss",
})
export class FormulaPopupComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  readonly Formulas = Formulas;

  showPopup = false;

  public evaSubscription: EvaSubscription;
  public formulaRecommended: string;
  public showFormulaPrices: boolean = false;

  whatsappLink = WHATSAPP_EXPERT_LINK;

  proFormula: Formula;
  ecoFormula: Formula;

  proOpen = false;
  ecoOpen = false;

  step: "plans" | "pay" | "success" = "plans";
  paymentMethod: "invoice" | "card" = "invoice";
  chosenFormula: Formulas = Formulas.EVA_PRO;
  checkoutInterval: "YEARLY" | "MONTHLY" = "YEARLY";
  openingPortal = false;

  get historicalOffer() {
    return this.evaSubscription?.status === "ACTIVE" ? this.evaSubscription.historical_offer : null;
  }

  get historicalFuturePrice(): { amount: number; standardAmount: number } | null {
    const offer = this.historicalOffer;
    if (!offer || offer.currency !== "CHF" || offer.amount === null) return null;

    const prices: Partial<Record<typeof offer.interval, Record<number, number>>> = {
      MONTHLY: { 19: 39, 49: 69, 79: 99 },
      YEARLY: { 190: 390, 490: 690, 790: 990 },
    };
    const amount = prices[offer.interval]?.[offer.amount];
    if (amount === undefined) return null;

    return { amount, standardAmount: offer.interval === "MONTHLY" ? 99 : 990 };
  }

  get historicalPriceSuffix(): string {
    const suffixes = {
      MONTHLY: "HT / mois",
      QUARTERLY: "HT / trimestre",
      SEMESTER: "HT / semestre",
      YEARLY: "HT / an",
      LIFETIME: "HT · paiement unique",
    };
    const interval = this.historicalOffer?.interval;
    return interval ? (suffixes[interval] ?? "HT") : "HT";
  }

  constructor(
    private evaSubscriptionService: EvaSubscriptionService,
    private evaService: EvaService,
  ) {
    this.buildFormulas();
  }

  ngOnInit(): void {
    this.evaSubscriptionService.currentSubscription$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((subscription) => {
        this.evaSubscription = subscription;
        this.buildFormulas();
      });

    this.evaSubscriptionService
      .getFormulaPopupObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ formulaRecommended, showPrices }) => {
        this.showPopup = true;
        this.formulaRecommended = formulaRecommended;
        this.showFormulaPrices = showPrices;
        this.proOpen = false;
        this.ecoOpen = false;
        this.step = "plans";
        this.paymentMethod = "invoice";
        this.chosenFormula = Formulas.EVA_PRO;
        this.checkoutInterval = "YEARLY";
      });
  }

  private buildFormulas(): void {
    const formulas = this.evaSubscriptionService.getAvailableFormulas();
    this.proFormula = formulas.find((f) => f.id === Formulas.EVA_PRO);
    this.ecoFormula = formulas.find((f) => f.id === Formulas.EVA_360);
  }

  closePopup(): void {
    this.showPopup = false;
  }

  toggleProDetails(): void {
    this.proOpen = !this.proOpen;
  }

  toggleEcoDetails(): void {
    this.ecoOpen = !this.ecoOpen;
  }

  get chosenFormulaObject(): Formula {
    return this.chosenFormula === Formulas.EVA_360 ? this.ecoFormula : this.proFormula;
  }

  get canPayByCard(): boolean {
    // EVA 360 est sur-mesure : pas de prix Stripe fixe, donc pas de paiement carte en self-service.
    return (
      this.chosenFormula === Formulas.EVA_PRO &&
      (!this.historicalOffer || !!this.evaSubscription?.stripe_subscription_id)
    );
  }

  goToPaymentStep(formula: Formulas): void {
    const target = formula === Formulas.EVA_360 ? this.ecoFormula : this.proFormula;
    if (target?.active) return;
    this.chosenFormula = formula;
    this.checkoutInterval = "YEARLY";
    this.paymentMethod = "invoice";
    this.step = "pay";
  }

  backToPlans(): void {
    this.step = "plans";
  }

  pickPaymentMethod(method: "invoice" | "card"): void {
    if (method === "card" && !this.canPayByCard) return;
    this.paymentMethod = method;
  }

  confirmPayment(): void {
    if (this.paymentMethod === "card" && this.canPayByCard) {
      this.subscribePro();
    } else {
      this.requestInvoice();
    }
  }

  requestInvoice(): void {
    const formulaName = this.chosenFormulaObject?.name ?? this.chosenFormula;
    const requestLabel =
      this.chosenFormula === Formulas.EVA_PRO
        ? `${formulaName} — ${this.checkoutInterval === "YEARLY" ? "paiement annuel" : "paiement mensuel"}`
        : formulaName;
    this.evaSubscriptionService.requestInvoice(requestLabel).subscribe({
      next: () => {
        this.step = "success";
      },
      error: (error) => {
        this.evaService.showError(
          "Une erreur est survenue lors de l'envoi de votre demande. Merci de réessayer ou de nous contacter à support@eva-it.ch.",
          "requestInvoice",
          error,
        );
      },
    });
  }

  subscribePro(): void {
    if (this.proFormula?.active) return;

    if (this.historicalOffer && !this.evaSubscription?.stripe_subscription_id) {
      this.requestInvoice();
      return;
    }

    if (this.evaSubscription?.stripe_subscription_id) {
      // Abonnement Stripe déjà existant (ex: reprise après annulation) -> portail Stripe.
      this.manageSubscription();
      return;
    }

    // Le choix du tarif annuel se fait via l'upsell dans Stripe Checkout.
    this.evaSubscriptionService.activateSubscription(Formulas.EVA_PRO, "MONTHLY");
  }

  manageSubscription(): void {
    if (this.openingPortal) return;
    if (!this.evaSubscription?.stripe_subscription_id) {
      window.open(this.whatsappLink, "_blank", "noopener,noreferrer");
      return;
    }
    this.openingPortal = true;
    this.evaSubscriptionService
      .modifySubscription()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => window.open(result.link, "_self"),
        error: (error) => {
          this.openingPortal = false;
          this.evaService.showError("Impossible d'ouvrir la gestion de votre abonnement.", "manageSubscription", error);
        },
        complete: () => {
          this.openingPortal = false;
        },
      });
  }

  demoEva(): void {
    window.open(
      "https://contact.eva-it.ch/meetings/franck-lauer/visio-presentation-de-loffre-installation-de-eva",
      "_blank",
    );
  }

  resetAccount(): void {}
}
